import React, { useEffect, useState } from 'react'
import { Tabs, TabsProps } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'

/**
 * CustomTabs component
 *
 * A wrapper around react-bootstrap Tabs that synchronizes the active tab with URL query parameters.
 * This allows users to share URLs with specific tabs selected and enables browser back/forward navigation.
 *
 * @example
 * ```tsx
 * <CustomTabs
 *   defaultActiveKey="tab1"
 *   queryParamName="activeTab"
 *   validTabs={['tab1', 'tab2', 'tab3']}
 * >
 *   <Tab eventKey="tab1" title="Tab 1">
 *     Content 1
 *   </Tab>
 *   <Tab eventKey="tab2" title="Tab 2">
 *     Content 2
 *   </Tab>
 * </CustomTabs>
 * ```
 */

export interface CustomTabsProps extends TabsProps {
  /**
   * The name of the query parameter to use for storing the active tab in the URL.
   * @default 'activeTab'
   */
  queryParamName?: string

  /**
   * An optional list of valid tab keys. If provided, only these tab keys will be
   * considered valid when reading from the URL. This prevents invalid tabs from being set.
   */
  validTabs?: string[]

  /**
   * Callback function that is called when the active tab changes.
   * Receives the new active tab key as a parameter.
   */
  onTabChange?: (activeKey: string) => void
}

const CustomTabs: React.FC<CustomTabsProps> = ({
  queryParamName = 'activeTab',
  validTabs,
  defaultActiveKey,
  onTabChange,
  onSelect,
  activeKey: controlledActiveKey,
  children,
  ...otherProps
}) => {
  const history = useHistory()
  const [internalActiveKey, setInternalActiveKey] = useState<string>(() => {
    // Initialize from URL query param if available
    const queryParams = new URLSearchParams(history.location.search)
    const tabFromQuery = queryParams.get(queryParamName)

    // Validate against validTabs if provided
    if (tabFromQuery && (!validTabs || validTabs.includes(tabFromQuery))) {
      return tabFromQuery
    }

    // Fall back to defaultActiveKey or first valid tab
    return (defaultActiveKey as string) || (validTabs && validTabs[0]) || ''
  })

  // Use controlled activeKey if provided, otherwise use internal state
  const activeKey =
    controlledActiveKey !== undefined ? controlledActiveKey : internalActiveKey

  // Call onTabChange on mount to sync parent component state with URL
  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search)
    const tabFromQuery = queryParams.get(queryParamName)

    // Determine the active tab
    const initialTab =
      tabFromQuery && (!validTabs || validTabs.includes(tabFromQuery))
        ? tabFromQuery
        : (defaultActiveKey as string) || (validTabs && validTabs[0]) || ''

    // Always call onTabChange on mount to ensure parent state is synced
    if (onTabChange && initialTab) {
      onTabChange(initialTab)
    }
  }, []) // Only run on mount

  // Sync URL when active tab changes
  useEffect(() => {
    if (activeKey) {
      const params = new URLSearchParams(history.location.search)
      const currentTabInUrl = params.get(queryParamName)

      if (currentTabInUrl !== activeKey) {
        params.set(queryParamName, activeKey as string)
        history.replace({
          pathname: history.location.pathname,
          search: params.toString(),
        })
      }
    }
  }, [activeKey, history, queryParamName])

  // Listen to URL changes (e.g., browser back/forward)
  useEffect(() => {
    const unlisten = history.listen(location => {
      const params = new URLSearchParams(location.search)
      const tabFromQuery = params.get(queryParamName)

      // Determine which tab should be active
      let targetTab: string | null = null

      if (tabFromQuery && (!validTabs || validTabs.includes(tabFromQuery))) {
        targetTab = tabFromQuery
      } else if (!tabFromQuery) {
        // If no tab in URL, fall back to default
        targetTab =
          (defaultActiveKey as string) || (validTabs && validTabs[0]) || ''
      }

      if (targetTab) {
        if (controlledActiveKey === undefined) {
          setInternalActiveKey(targetTab)
        }
        if (onTabChange) {
          onTabChange(targetTab)
        }
      }
    })

    return () => unlisten()
  }, [
    history,
    queryParamName,
    validTabs,
    onTabChange,
    controlledActiveKey,
    defaultActiveKey,
  ])

  const handleSelect = (eventKey: string | null) => {
    if (!eventKey) return

    // Validate against validTabs if provided
    if (validTabs && !validTabs.includes(eventKey)) {
      return
    }

    // Update internal state only if not controlled
    if (controlledActiveKey === undefined) {
      setInternalActiveKey(eventKey)
    }

    // Call the custom callback if provided
    if (onTabChange) {
      onTabChange(eventKey)
    }

    // Call the original onSelect if provided
    if (onSelect) {
      onSelect(eventKey, null as any)
    }
  }

  return (
    <Tabs {...otherProps} activeKey={activeKey} onSelect={handleSelect}>
      {children}
    </Tabs>
  )
}

export default CustomTabs
